import { MibMoment, mibMs } from '../../foundations';
import { startOfDay } from '../../internal/dates.utils';

type DatePart = 'day' | 'month' | 'year';

/** The order the locale writes a numeric date in, and the separator it puts between the parts. */
function dateShape(locale: string | undefined): { order: DatePart[]; separator: string } {
  const parts = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(
    new Date(2026, 10, 22),
  );
  const order = parts.filter((p) => p.type === 'day' || p.type === 'month' || p.type === 'year').map((p) => p.type);
  const separator = parts.find((p) => p.type === 'literal')?.value.trim() || '/';
  return { order: order as DatePart[], separator };
}

/** What a date field shows when it is empty, such as `MM/DD/YYYY`. */
export function datePlaceholder(locale: string | undefined): string {
  const { order, separator } = dateShape(locale);
  const marks: Record<DatePart, string> = { day: 'DD', month: 'MM', year: 'YYYY' };
  return order.map((p) => marks[p]).join(separator);
}

export function formatDate(date: Date, locale: string | undefined): string {
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

/**
 * Reads a date typed in the locale's order, with any separator. `null` for an empty text, `undefined`
 * for a text that is not a day of the calendar, or whose year is short of four digits with `fullYear`.
 */
export function parseDate(text: string, locale: string | undefined, fullYear = false): Date | null | undefined {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const tokens = trimmed.split(/\D+/).filter(Boolean);
  if (tokens.length !== 3) return undefined;
  const { order } = dateShape(locale);
  if (fullYear && tokens[order.indexOf('year')].length !== 4) return undefined;
  const value = Object.fromEntries(order.map((p, i) => [p, Number(tokens[i])])) as Record<DatePart, number>;
  const year = value.year < 100 ? 2000 + value.year : value.year;
  const date = new Date(year, value.month - 1, value.day);
  const real = date.getFullYear() === year && date.getMonth() === value.month - 1 && date.getDate() === value.day;
  return real ? date : undefined;
}

/** With `twoDigits`, the hour is padded as a native time field writes it, and as the mask types it. */
export function formatTime(date: Date, locale: string | undefined, twoDigits = false): string {
  return new Intl.DateTimeFormat(locale, { hour: twoDigits ? '2-digit' : 'numeric', minute: '2-digit' }).format(date);
}

export function uses12Hours(locale: string | undefined): boolean {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12 ?? false;
}

/**
 * Fills parts of the given lengths with the digits typed. A full part moves on to the next one, and so
 * does a separator typed after a part, which pads a lone digit with a zero; letters are ignored.
 */
function maskParts(text: string, lengths: number[], separator: string): string {
  const parts = [''];
  for (const char of text) {
    const at = parts.length - 1;
    if (/\d/.test(char)) {
      if (parts[at].length < lengths[at]) parts[at] += char;
      else if (at < lengths.length - 1) parts.push(char);
    } else if (!/[a-z]/i.test(char) && parts[at] && at < lengths.length - 1) {
      if (parts[at].length === 1 && lengths[at] === 2) parts[at] = '0' + parts[at];
      parts.push('');
    }
  }
  return parts.join(separator);
}

/** The digits typed, written in the locale's order, with its separators. */
export function maskDate(text: string, locale: string | undefined): string {
  const { order, separator } = dateShape(locale);
  return maskParts(
    text,
    order.map((p) => (p === 'year' ? 4 : 2)),
    separator,
  );
}

/**
 * The digits typed, written as `HH:MM`, and on a 12 hour clock an `a` or a `p` as AM or PM. While
 * deleting, a half-erased AM or PM is dropped rather than written back.
 */
export function maskTime(text: string, locale: string | undefined, deleting = false): string {
  const clock = maskParts(text.replace(/[\sa-z.]+$/i, ''), [2, 2], ':');
  if (!/\d/.test(clock) || !uses12Hours(locale)) return clock;
  const letters = text.replace(/[^a-z]/gi, '').toLowerCase();
  const half = deleting ? (/^(a|p)m$/.exec(letters)?.[1] ?? null) : (/[ap]/.exec(letters)?.[0] ?? null);
  return half ? `${clock} ${half === 'a' ? 'AM' : 'PM'}` : clock;
}

/**
 * Reads a time such as `14:30`, `2:30 pm`, `2pm` or `1430`. Returns the minutes since midnight, `null`
 * for an empty text, `undefined` for a text that is not a time.
 */
export function parseTime(text: string): number | null | undefined {
  const trimmed = text.trim().toLowerCase();
  if (!trimmed) return null;
  const match = /^(\d{1,2})(?:\D?(\d{2}))?\s*(a|p)?\.?\s*m?\.?$/.exec(trimmed);
  if (!match) return undefined;
  let hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  const half = match[3];
  if (minutes > 59) return undefined;
  if (half) {
    if (hours < 1 || hours > 12) return undefined;
    hours = (hours % 12) + (half === 'p' ? 12 : 0);
  } else if (hours > 23) {
    return undefined;
  }
  return hours * 60 + minutes;
}

/** The minutes since midnight of a moment, or of an `HH:mm` text. `null` when there is none. */
export function minutesOf(at: MibMoment | null | undefined): number | null {
  if (at === null || at === undefined || at === '') return null;
  if (typeof at === 'string' && /^\d{1,2}:\d{2}$/.test(at)) return parseTime(at) ?? null;
  const ms = mibMs(at);
  if (Number.isNaN(ms)) return null;
  const date = new Date(ms);
  return date.getHours() * 60 + date.getMinutes();
}

/** The day a moment falls on, at midnight; a bare `YYYY-MM-DD` is that local day. `null` when there is none. */
export function dayOf(at: MibMoment | null | undefined): Date | null {
  if (at === null || at === undefined || at === '') return null;
  // Date.parse reads a bare date as UTC midnight, which is the day before behind UTC.
  const bare = typeof at === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(at) : null;
  if (bare) {
    const day = new Date(Number(bare[1]), Number(bare[2]) - 1, Number(bare[3]));
    return isoDay(day) === at ? day : null;
  }
  const ms = mibMs(at);
  return Number.isNaN(ms) ? null : startOfDay(new Date(ms));
}

/** The local calendar day of `day` as `YYYY-MM-DD`, the same string in every time zone. */
export function isoDay(day: Date): string {
  const pad = (n: number, length = 2): string => String(n).padStart(length, '0');
  return `${pad(day.getFullYear(), 4)}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

/** The minutes of every slot from `min` to `max`, `interval` apart from midnight. */
export function timeSlots(interval: number, min: number | null, max: number | null): number[] {
  const step = Math.max(1, Math.round(interval));
  const slots: number[] = [];
  for (let m = 0; m < 24 * 60; m += step) {
    if ((min === null || m >= min) && (max === null || m <= max)) slots.push(m);
  }
  return slots;
}

/** `day` at `minutes` past its midnight. */
export function atMinutes(day: Date, minutes: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(minutes / 60), minutes % 60);
}

export function addMonths(at: Date, months: number): Date {
  const target = new Date(at.getFullYear(), at.getMonth() + months, 1);
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(at.getDate(), last));
}
